import { useEffect, useRef } from 'react'
import { Box } from '@mui/material'
import { GRID_H, GRID_W, paintHeat } from './gazeHeat'

export interface HeatField {
  density: Float32Array
  /** The segment's peak density: the top of the colour scale. */
  peak: number
}

/**
 * Gaze heat map over the stimulus player: paints a density grid from
 * `gazeHeat.ts` and lets the browser scale it up smoothly. Fills its
 * positioned parent; never takes clicks.
 */
export default function GazeHeatLayer({ field }: { field: HeatField }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    // Set on the element itself: on a MUI Box, width/height props are CSS, not
    // the canvas resolution, which would stay at the default 300×150.
    if (canvas.width !== GRID_W) canvas.width = GRID_W
    if (canvas.height !== GRID_H) canvas.height = GRID_H
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const img = ctx.createImageData(GRID_W, GRID_H)
    paintHeat(field.density, field.peak, img)
    ctx.putImageData(img, 0, 0)
  }, [field])

  return (
    <Box
      component="canvas"
      ref={canvasRef}
      aria-hidden
      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    />
  )
}
